/**
 * FIERE (FILO-67) — le regole dei task di follow-up dei contatti rapidi: per ognuno dei sei livelli
 * di interesse, se all'importazione nasce il task, con che priorità e tipo, ed entro quanti giorni
 * lavorativi dalla data di inizio dei richiami. Una tabella di sei righe fisse, una per livello.
 *
 * Le regole arrivano sempre tutte e sei: le predefinite se la company non le ha personalizzate.
 * Spegnere una riga non cancella priorità, giorni e tipo, che restano per quando la si riaccende.
 * Solo l'amministratore modifica; gli altri vedono le regole in sola lettura.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useAppSelector } from '../../store/hooks';
import { extractError } from '../../store/thunkHelpers';
import { useToast } from '../useToast';
import {
  rapidContactTaskRulesApi,
  type RapidContactTaskRule,
  type RapidContactTaskRules,
} from '../../entities/rapidContact/rapidContactApi';
import { TASK_PRIORITIES, TASK_TYPES } from '../../entities/task/taskTypes';
import { taskPriorityToDto, taskTypeToDto } from '../../entities/task/taskMappers';

/** I giorni massimi di una regola: lo stesso limite del backend (RapidContactTaskRule.MAX_DAYS). */
const MAX_DAYS = 365;

const sameRules = (a: RapidContactTaskRule[], b: RapidContactTaskRule[]) => JSON.stringify(a) === JSON.stringify(b);

export function FairSettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const isAdmin = useAppSelector((state) => state.auth.user?.administrator ?? false);

  // Le regole come le ha il server, e quelle in modifica: il confronto dice se c'è da salvare.
  const [saved, setSaved] = useState<RapidContactTaskRules | null>(null);
  const [rules, setRules] = useState<RapidContactTaskRule[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    rapidContactTaskRulesApi.get()
      .then((r) => { if (active) { setSaved(r); setRules(r.rules); } })
      .catch((e) => toast({ severity: 'error', message: extractError(e) }));
    return () => { active = false; };
  }, [toast]);

  const priorities = useMemo(
    () => TASK_PRIORITIES.map((p) => ({ value: taskPriorityToDto(p.value), label: t(p.label) })),
    [t],
  );
  const types = useMemo(
    () => TASK_TYPES.map((p) => ({ value: taskTypeToDto(p.value), label: t(p.label) })),
    [t],
  );

  const changed = saved !== null && !sameRules(saved.rules, rules);
  const daysValid = rules.every((r) => Number.isInteger(r.days) && r.days >= 0 && r.days <= MAX_DAYS);

  function update(index: number, patch: Partial<RapidContactTaskRule>) {
    setRules((current) => current.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  async function run(action: () => Promise<RapidContactTaskRules>, success: string) {
    setBusy(true);
    try {
      const r = await action();
      setSaved(r);
      setRules(r.rules);
      toast({ severity: 'success', message: t(success) });
    } catch (e) {
      toast({ severity: 'error', message: extractError(e) });
    } finally {
      setBusy(false);
    }
  }

  const readOnly = !isAdmin || busy;

  return (
    <div className="fair-settings p-md">
      <h2>{t('settings.fairs.title')}</h2>
      <p className="text-muted">{t('settings.fairs.hint')}</p>

      {saved === null ? (
        <p className="text-muted">{t('common.loading')}</p>
      ) : (
        <div className="ltable-wrap">
          <div className="ltable__head">
            <span>{t('settings.fairs.columns.level')}</span>
            <span>{t('settings.fairs.columns.task')}</span>
            <span>{t('settings.fairs.columns.priority')}</span>
            <span>{t('settings.fairs.columns.days')}</span>
            <span>{t('settings.fairs.columns.type')}</span>
          </div>
          {rules.map((rule, index) => (
            <div key={rule.level} className={`lrow${rule.task ? '' : ' fair-settings__row--off'}`}>
              <span className="fair-settings__level">{t(`settings.fairs.levels.${rule.level}`)}</span>
              <span>
                <Checkbox
                  checked={rule.task}
                  disabled={readOnly}
                  onChange={(e) => update(index, { task: e.target.checked })}
                  slotProps={{ input: { 'aria-label': t('settings.fairs.columns.task') } }}
                />
              </span>
              <TextField
                select size="small" value={rule.priority} disabled={readOnly || !rule.task}
                onChange={(e) => update(index, { priority: Number(e.target.value) })}
                aria-label={t('settings.fairs.columns.priority')}
              >
                {priorities.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
              <TextField
                type="number" size="small" value={rule.days} disabled={readOnly || !rule.task}
                onChange={(e) => update(index, { days: e.target.value === '' ? Number.NaN : Number(e.target.value) })}
                error={!(Number.isInteger(rule.days) && rule.days >= 0 && rule.days <= MAX_DAYS)}
                slotProps={{ htmlInput: { min: 0, max: MAX_DAYS, step: 1, 'aria-label': t('settings.fairs.columns.days') } }}
              />
              <TextField
                select size="small" value={rule.type} disabled={readOnly || !rule.task}
                onChange={(e) => update(index, { type: Number(e.target.value) })}
                aria-label={t('settings.fairs.columns.type')}
              >
                {types.map((p) => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
            </div>
          ))}
        </div>
      )}

      {saved !== null && (isAdmin ? (
        <div className="flex gap-sm fair-settings__actions">
          <Button
            variant="contained"
            onClick={() => run(() => rapidContactTaskRulesApi.save(rules), 'settings.fairs.saved')}
            disabled={!changed || !daysValid || busy}
          >
            {t('common.save')}
          </Button>
          <Button
            variant="text"
            onClick={() => run(() => rapidContactTaskRulesApi.reset(), 'settings.fairs.restored')}
            disabled={!saved.custom || busy}
          >
            {t('settings.fairs.reset')}
          </Button>
        </div>
      ) : (
        <p className="text-muted text-sm">{t('settings.fairs.readonly')}</p>
      ))}
    </div>
  );
}
